import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDateFilterBar } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatUSDate } from "@/lib/date-utils"
import { teamLogsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface LogEntry {
  id: number
  log_date: string | null
  content: string
  added_by: string | null
}

interface Props {
  employee: { id: number; name: string }
  logs: LogEntry[]
  start_date: string
  end_date: string
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDate(dateStr: string | null): string {
  return formatUSDate(dateStr)
}

/* ── Constants ──────────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Team", href: "/team" },
  { title: "Employee Logs", href: teamLogsPath() },
]

/* ── Component ──────────────────────────────────────────── */

export default function Logs({ logs, start_date, end_date }: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    router.get(teamLogsPath(), params, { preserveState: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Employee Logs" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Employee Logs</h1>

        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        {logs.length === 0 ? (
          <div className="rounded border border-gray-200 bg-gray-50 px-6 py-8 text-center">
            <p className="text-hpi-med-gray text-sm">No log entries found.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {logs.map((log) => (
              <li
                key={log.id}
                className="border-hpi-light-gray rounded border bg-white px-4 py-3"
              >
                <div className="text-hpi-dark-gray text-sm font-medium">
                  {formatDate(log.log_date)}
                  {log.added_by && (
                    <span className="text-hpi-med-gray">
                      {" "}
                      by {log.added_by}
                    </span>
                  )}
                </div>
                <div className="text-hpi-dark-gray mt-1 text-sm whitespace-pre-wrap">
                  {log.content}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppLayout>
  )
}
